import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import CollapsiblePanel, {
  CollapsiblePanelStyleVariant,
} from '@payaca/components/collapsiblePanel/CollapsiblePanel';
import CompanySettingsBranding from './CompanySettingsBranding';
import CompanySettingsCompanyInfo from './CompanySettingsCompanyInfo';
import CompanySettingsGettingPaid from './CompanySettingsGettingPaid';
import CompanySettingsTerms from './CompanySettingsTerms';

import { actions as accountSettingsActions } from '@/api/accountSettings';
import { useSelector } from '@/api/state';
import { actions as userActions } from '@/api/users';
import * as accountActions from '@payaca/store/account/accountActions';

import { parseAccount } from '@/helpers/userHelper';
import { getUrlSearchParam } from '@payaca/helpers/urlHelper';

import { useCanUpdateAccount } from '@/hooks/useCanUpdateAccount';
import { getRegionalTextString } from '@payaca/helpers/internationalHelper';
import { RegionalStrings } from '@payaca/types/internationalTypes';
import ContactUsModal from '../contactUsModal/ContactUsModal';
import './CompanySettings.sass';
import CompanySettingsTaxRates from './CompanySettingsTaxRates';

const CompanySettings: FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const isAdmin = useCanUpdateAccount();

  const [showContactUsModal, setShowContactUsModal] = useState(false);

  const currentAccount = useSelector(
    (state: any) => state.users?.myProfile?.accounts[0]
  );

  const emailCustomisationRegionalLabel = useMemo(
    () =>
      getRegionalTextString(
        currentAccount.region,
        RegionalStrings.EMAIL_CUSTOMISATION
      ),
    [currentAccount]
  );

  const pendingAccount = useSelector(
    (state: any) => state.accountSettings.pendingAccount
  );

  const taxRates = useSelector((state) => state.taxRates.store);

  const sections = useMemo(
    () => [
      {
        title: 'Company info',
        urlSlug: 'company_info',
        children: <CompanySettingsCompanyInfo readOnly={!isAdmin} />,
        watchSize: pendingAccount,
      },
      {
        title: 'Branding',
        urlSlug: 'branding',
        children: <CompanySettingsBranding readOnly={!isAdmin} />,
      },
      {
        title: 'Getting paid',
        urlSlug: 'getting_paid',
        children: <CompanySettingsGettingPaid readOnly={!isAdmin} />,
      },
      {
        title: 'Tax rates',
        urlSlug: 'tax_rates',
        children: <CompanySettingsTaxRates readOnly={!isAdmin} />,
        watchSize: taxRates,
      },
      {
        title: 'Terms',
        urlSlug: 'terms',
        children: <CompanySettingsTerms readOnly={!isAdmin} />,
      },
    ],
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: emailCustomisationRegionalLabel
    [isAdmin, pendingAccount, taxRates, emailCustomisationRegionalLabel]
  );

  const defaultOpenSectionIndex = useMemo(() => {
    const queryTabName = getUrlSearchParam(location.search, 'tab');
    if (!queryTabName) {
      return null;
    }
    const chosenSectionIndex = sections.findIndex(
      (section) => section.urlSlug === queryTabName?.toLowerCase()
    );
    return chosenSectionIndex >= 0 ? chosenSectionIndex : null;
  }, [location, sections]);

  const [openSectionIndex, setOpenSectionIndex] = useState<number | null>(
    defaultOpenSectionIndex
  );

  useEffect(() => {
    dispatch(userActions.getBusinessAttachments());
    dispatch(userActions.getAccountTerms(currentAccount.id));
    dispatch(accountActions.requestGetEmailTemplates(currentAccount.id, true));

    return () => {
      dispatch(accountSettingsActions.clearAccountPendingChanges());
    };
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'dispatch', and 'currentAccount.id'
  }, []);

  const terms = useSelector((state: any) => state.users.terms);

  const emailTemplates = useSelector((state) => state.account.emailTemplates);
  const businessAttachments = useSelector(
    (state: any) => state.users.businessAttachments
  );

  useEffect(() => {
    dispatch(
      accountSettingsActions.storeAccountPendingChanges(
        parseAccount(currentAccount)
      )
    );
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [currentAccount]);

  useEffect(() => {
    dispatch(
      accountSettingsActions.storeAccountPendingBusinessAttachments(
        businessAttachments
      )
    );
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [businessAttachments]);

  useEffect(() => {
    dispatch(accountSettingsActions.storeAccountPendingTerms(terms));
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [terms]);

  useEffect(() => {
    if (emailTemplates) {
      dispatch(
        accountSettingsActions.storeAccountPendingEmailTemplates({
          sendEstimate: emailTemplates.sendEstimate,
          sendQuote: emailTemplates.sendQuote,
          sendInvoice: emailTemplates.sendInvoice,
        })
      );
    }
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [emailTemplates]);

  return (
    <div className="company-settings-container">
      {!isAdmin && (
        <div className="contact-admin-text">
          Please contact an admin to amend these details
        </div>
      )}
      {sections.map(({ title, children, watchSize }, index) => (
        <CollapsiblePanel
          key={index}
          title={title}
          isOpen={openSectionIndex === index}
          onOpen={() => setOpenSectionIndex(index)}
          onClose={() => setOpenSectionIndex(null)}
          styleVariant={CollapsiblePanelStyleVariant.OUTSIZE}
          watchSize={watchSize}
        >
          {children}
        </CollapsiblePanel>
      ))}
      <ContactUsModal
        isOpen={showContactUsModal}
        onClose={() => setShowContactUsModal(false)}
      />
    </div>
  );
};

export default CompanySettings;
